import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BlockIcon from '@mui/icons-material/Block';
import { Progress, ErrorPanel } from '@backstage/core-components';
import { CampaignCellStatus } from '@internal/backstage-plugin-bidone-backend';
import {
  useAddExemption,
  useCampaignExemptions,
  useCampaignProgress,
  useRemoveExemption,
  useSetCheckCompletion,
} from '../../hooks/useCampaigns';

const pct = (pass: number, total: number) =>
  total === 0 ? 0 : Math.round((pass / total) * 100);

const StatusIcon = ({ status }: { status: CampaignCellStatus }) => {
  switch (status) {
    case 'pass':
      return (
        <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
      );
    case 'fail':
      return <CancelIcon fontSize="small" sx={{ color: 'error.main' }} />;
    case 'pending':
      return (
        <Tooltip title="Awaiting attestation">
          <RadioButtonUncheckedIcon
            fontSize="small"
            sx={{ color: 'text.disabled' }}
          />
        </Tooltip>
      );
    case 'exempt':
      return (
        <Tooltip title="Exempt">
          <BlockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Not applicable (excluded by this check's filter)">
          <Typography variant="body2" color="textSecondary">
            —
          </Typography>
        </Tooltip>
      );
  }
};

type CampaignProgressMatrixProps = {
  campaignId: string;
  // When true (campaign not ended), manual cells are clickable and components
  // can be exempted.
  editable: boolean;
};

// The components-x-checks matrix for a campaign. Fact checks render a read-only
// pass/fail icon; manual checks render a checkbox the owner toggles.
export const CampaignProgressMatrix = ({
  campaignId,
  editable,
}: CampaignProgressMatrixProps) => {
  const { data: progress, isFetching, error } = useCampaignProgress(campaignId);
  const { data: exemptions } = useCampaignExemptions(campaignId);
  const setCompletion = useSetCheckCompletion();
  const addExemption = useAddExemption();
  const removeExemption = useRemoveExemption();

  if (error) return <ErrorPanel error={error as Error} />;
  if (!progress) return <Progress />;

  if (progress.rows.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No components in scope yet.{' '}
        {progress.membershipMode === 'live'
          ? 'Set a targeting filter for this campaign.'
          : 'Curate the snapshot membership for this campaign.'}
      </Typography>
    );
  }

  const wholeExempt = new Set(
    (exemptions ?? [])
      .filter(e => e.checkId === null)
      .map(e => e.componentRef),
  );
  const manualByCheckId = new Map(
    progress.checks.map(c => [c.id, c.kind === 'manual']),
  );

  const toggleManual = (
    checkId: string,
    componentRef: string,
    status: CampaignCellStatus,
  ) =>
    setCompletion.mutate({
      campaignId,
      checkId,
      componentRef,
      done: status !== 'pass',
    });

  const toggleExempt = (componentRef: string) => {
    if (wholeExempt.has(componentRef)) {
      removeExemption.mutate({ campaignId, componentRef, checkId: null });
    } else {
      addExemption.mutate({ campaignId, componentRef, checkId: null });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Chip
          color="primary"
          label={`${pct(progress.overall.pass, progress.overall.total)}% complete (${progress.overall.pass}/${progress.overall.total})`}
        />
        {progress.perCheck.map(c => (
          <Chip
            key={c.id}
            size="small"
            variant="outlined"
            label={`${c.name}: ${c.pass}/${c.total}`}
          />
        ))}
      </Box>
      {isFetching && <LinearProgress sx={{ mb: 1 }} />}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              {progress.checks.map(c => (
                <TableCell key={c.id} align="center">
                  {c.name}
                  {c.kind === 'manual' ? ' (manual)' : ''}
                </TableCell>
              ))}
              {editable && <TableCell align="right">Scope</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {progress.rows.map(row => {
              const exempt = wholeExempt.has(row.componentRef);
              return (
                <TableRow
                  key={row.componentRef}
                  sx={exempt ? { opacity: 0.5 } : undefined}
                >
                  <TableCell>
                    <Typography variant="body2">{row.title}</Typography>
                    {row.owner && (
                      <Typography variant="caption" color="textSecondary">
                        {row.owner}
                      </Typography>
                    )}
                  </TableCell>
                  {progress.checks.map(c => {
                    const status = row.cells[c.id] ?? 'na';
                    const isManual = manualByCheckId.get(c.id);
                    const clickable =
                      editable &&
                      isManual &&
                      (status === 'pass' || status === 'pending');
                    return (
                      <TableCell key={c.id} align="center">
                        {clickable ? (
                          <Checkbox
                            size="small"
                            checked={status === 'pass'}
                            onChange={() =>
                              toggleManual(c.id, row.componentRef, status)
                            }
                          />
                        ) : (
                          <StatusIcon status={status} />
                        )}
                      </TableCell>
                    );
                  })}
                  {editable && (
                    <TableCell align="right">
                      <Tooltip
                        title={
                          exempt
                            ? 'Bring back into scope'
                            : 'Exempt this component'
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleExempt(row.componentRef)}
                        >
                          <BlockIcon
                            fontSize="small"
                            color={exempt ? 'disabled' : 'inherit'}
                          />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
